import * as React from "react";
import styled, { css } from "styled-components";
import { select } from "@hubper/theme";

import Text from "../Text";

const HEIGHT = 50;

export type InputContainerStates = {
  focus?: boolean;
  disabled?: boolean;
  error?: boolean | string;
};

const inputFocusStyle = css<InputContainerStates>`
  border: 1px solid
    ${p => select.color(p.error ? ["error", 5] : ["grey", 3], p)};
`;

const inputHoverStyle = css<InputContainerStates>`
  border: 1px solid
    ${p => select.color(p.error ? ["error", 5] : ["grey", 3], p)};
`;

const inputDashedStyle = css`
  border: 1px dashed ${select.color(["grey", 5])};

  &:hover {
    border: 1px dashed ${select.color(["grey", 8])};
  }
`;

const inputDisabledStyle = css`
  background-color: ${select.color(["grey", 0])};
`;

const inputErrorStyle = css`
  background-color: white;
  border: 1px solid ${select.color(["error", 3])};
`;

export const inputContainerStyle = css<
  InputContainerStates & { small?: boolean; dashed?: boolean }
>`
  height: ${p => (p.small ? 42 : HEIGHT)}px;
  border: 1px solid ${select.color(["grey", 1])};
  border-radius: ${select.radius("light")};
  transition: border-color ${select.speed("fast")};
  padding-left: ${select.space(3)};
  padding-right: ${select.space(3)};

  ${p => p.focus && inputFocusStyle}
  &:focus {
    ${inputFocusStyle}
  }

  ${p => p.disabled && inputDisabledStyle}
  &:disabled {
    ${inputDisabledStyle}
  }

  &:hover {
    ${p => !p.disabled && inputHoverStyle}
  }

  ${p => p.dashed && inputDashedStyle}

  ${p => p.error && !p.focus && inputErrorStyle}
`;

export const Container = styled.div<
  {
    stretch?: boolean;
    small?: boolean;
    dashed?: boolean;
  } & InputContainerStates
>`
  background-color: white;
  ${inputContainerStyle};
  ${p => p.stretch && "width: 100%;"}
  color: ${select.color(["grey", 9])};
  font-family: ${select.font("Lato")};
  position: relative;
  display: flex;
  align-items: center;
`;

export const Label = styled(Text)`
  transition: ${select.speed("normal")};
  position: absolute;
  top: -7px;
  left: ${select.space(3)};
  z-index: 1;

  &::after {
    content: " ";
    position: absolute;
    left: -4px;
    right: -4px;
    top: 6px;
    height: 1px;
    background-color: white;
    z-index: -1;
  }
`;

export type InputContainerProps = {
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  small?: boolean;
  dashed?: boolean;
  onClick?: (evt: any) => void;
  stretch?: boolean;
  children?: React.ReactNode;
} & InputContainerStates;

function InputContainer(
  {
    style,
    className,
    disabled,
    error,
    label,
    focus,
    stretch,
    onClick,
    children,
    dashed,
    small
  }: InputContainerProps,
  ref: any
) {
  const labelColorType = error ? "error" : "grey";
  const labelColorIndex = focus ? 6 : 4;

  return (
    <Container
      stretch={stretch}
      className={className}
      ref={ref}
      focus={focus}
      disabled={disabled}
      error={error}
      onClick={onClick}
      style={style}
      dashed={dashed}
      small={small}
    >
      {children}
      {label && (
        <Label c={[labelColorType, labelColorIndex]} fs={-2} onClick={onClick}>
          {label}
        </Label>
      )}
      {error && typeof error === "string" && (
        <Text
          fs={-2}
          c={["error", 4]}
          style={{ top: "calc(100% + 6px)", left: 16, position: "absolute" }}
        >
          {error}
        </Text>
      )}
    </Container>
  );
}

export default React.forwardRef(InputContainer);
